import AbstractFilterControl from "./AbstractFilterControl";

import type { Criteria } from "@esWebAPI/es/model/Criteria";
import type LayerViewModel from "@esWebAPI/es/map/LayerViewModel";
import type { Attribute } from "@esWebAPI/es/model/Feature";
import _ from "lodash";

export default class CheckboxFilterControl extends AbstractFilterControl {
    private isBoolean: boolean;
    private rangeValues: Record<string, number>;
    private values;
    private count: number;

    private _debouncedOnValueChange = _.debounce(this.onToggleValue.bind(this), 300);

    constructor(layerViewModel: LayerViewModel, attribute: Attribute, range) {
        super(layerViewModel, "Checkbox", attribute);

        this.isBoolean = attribute.attrType === "BOOLEAN";
        this.rangeValues = range.values;
        // Default selected value should be true
        this.values = this.initValues(this.rangeValues, this.isBoolean, true);
        this.count = this.values.length;
    }

    private initValues(rangeValues: Record<string, number>, isBoolean: boolean, selected: boolean) {
        return Object.keys(rangeValues).sort().map((key) => {
            return {
                name: key,
                text: this.repr(key, isBoolean),
                count: rangeValues[key],
                selected: !!selected
            };
        });
    }

    private repr(text: string, isBoolean: boolean): string {
        let repr;
        if (text === "") {
            repr = $i18n('_Blank');
        } else if (isBoolean) {
            repr = parseInt(text) ? $i18n('True') : $i18n('False');
        } else {
            repr = text;
        }

        return repr;
    }

    private mergeCriteria(criteria: Criteria[], attr: string): Criteria | Criteria[] {
        if (!CheckboxFilterControl.isMultipleFilter(criteria)) {
            console.error("criteria is not an array");
            return criteria;
        }

        const notEqual = criteria.find(function(crit) {
            return crit.$type === "NotEqual" && !crit.value;
        });

        const nullEqual = criteria.find(function(crit) {
            return crit.$type === "Equal" && !crit.value;
        });

        if (notEqual && nullEqual) { // UncheckCheckbox
            return [nullEqual, notEqual];
        }

        if (criteria.every((crit) => crit.$type === "All")) {
            return criteria[0];
        }

        const values = criteria.reduce((res, crit) => { return res.concat(this.getValuesFromCriteria(crit)); }, [] as string[]);
        const criteriaList: Criteria[] = values.map((value) => {
            return {
                $type: "Equal",
                value: value,
                attr: attr
            };
        });

        return {
            $type: "Or",
            criteria: criteriaList
        };
    }

    private getValuesFromCriteria(criteria: Criteria): string[] {
        if (criteria.$type !== "Or") {
            return []; // either All or None ... we can not infer what it means, depends of the context.
        }

        return criteria.criteria.map((crit) => crit.value == null ? "" : crit.value); // TOR9-14987 null values are internally represented as blank value
    }

    toCriteria(expand: boolean): Criteria {
        if (!expand && this.isAll()) {
            return {
                $type: "All",
                attr: this.getAttrName()
            };
        }

        const orCriteria = this.values.filter((value) => {
            return value.selected;
        }).map((value) => {
            let name = value.name;
            if (this.isBoolean) {
                name = (name === '') ? null : parseInt(name);
            }

            return {
                $type: "Equal",
                attr: this.getAttrName(),
                value: name
            };
        }, this);

        switch (orCriteria.length) {
            case 0: // none is selected, return criteria to have 0 results.
                return {
                    $type: "And",
                    criteria: [{
                        $type: "Equal",
                        attr: this.getAttrName(),
                        value: null
                    }, {
                        $type: "NotEqual",
                        attr: this.getAttrName(),
                        value: null
                    }]
                };
            case 1:
                return orCriteria[0]; // No use of Or for one criteria
            default:
                return {
                    $type: "Or",
                    criteria: orCriteria
                };
        }
    }

    fromCriteria(criteria: Criteria | Criteria[]) {
        if (CheckboxFilterControl.isMultipleFilter(criteria)) {
            criteria = this.mergeCriteria(criteria, this.getAttrName());
        }

        this.values = this.initValues(this.rangeValues, this.isBoolean, criteria.$type === "All");

        if (criteria.$type === "All") {
            this.count = this.values.length;
            return;
        }

        if (Array.isArray(criteria)) {
            if (CheckboxFilterControl.isUncheckCheckBox(criteria)) {
                this.count = 0;
                return;
            }

            console.error("Invalid criteria given");
            return;
        }

        let criterias;
        if (criteria.$type === "Or") {
            criterias = criteria.criteria;
        } else if (criteria.$type === "Equal") {
            criterias = [criteria];
        } else {
            console.error("Invalid criteria");
            return;
        }

        // create map of name to index for faster retrieval
        const map = this.values.reduce((map, value, index) => {
            map[value.name] = index;
            return map;
        }, {});

        this.count = 0;
        criterias.forEach((crit) => {
            if (crit.attr !== this.getAttrName()) {
                es.log.error("Criteria attribute and filter attribute not matching");
            }

            if (crit.$type !== "Equal") {
                es.log.error("Invalid criteria type for Checkbox");
            }

            let critValue = crit.value;
            if (critValue == null) {
                critValue = ''; // TOR9-14987 null values are internally represented as blank value
            }

            if (critValue in map) {
                const index = map[critValue];

                this.values[index].selected = true;
                this.count += 1;
            } else {
                // This can happen if a criteria has been created before a FeatureSource criteria change the range
                es.log.warn("[" + critValue + "] is not present in Checkbox range");
            }
        });
    }

    isAll() {
        return this.values.length === this.count;
    }

    public toggleValue(index: number) {
        this.values[index].selected = !this.values[index].selected;
        this.count += (this.values[index].selected ? 1 : -1);
        this._debouncedOnValueChange();
    }

    private onToggleValue() {
        this.eventChannel.fireEvent("onUpdate");
    }

    toggleAll() {
        var selected = !this.isAll();
        this.values.forEach((value) => {
            value.selected = selected;
        });
        this.count = selected ? this.values.length : 0;

        this._debouncedOnValueChange();
    }

    getValues() {
        return this.values.slice();
    }

    setRange(attributeRanges: Record<string, any>) {
        this.rangeValues = attributeRanges[this.getAttrName()].values;
    }

    match(filter: AbstractFilterControl): boolean {
        return this !== filter
                && this.attrName === filter.getAttrName()
                && this.isLocked() === filter.isLocked()
                && (filter.getType() === "Checkbox" || filter.getType() === "Dropdown");
    }

    merge(filter: AbstractFilterControl) {
        const criteria: Criteria[] = [this.toCriteria(true), filter.toCriteria(true)];
        this.fromCriteria(criteria);
    }

    // es.control.CheckboxFilter = CheckboxFilter;

    static supportCriteria(criteria: Criteria | Criteria[], attribute: Attribute, range) {
        if (!attribute || !range) {
            return false;
        }

        return (CheckboxFilterControl.isMultipleFilter(criteria) || CheckboxFilterControl.isCheckboxFilter(criteria))
            && CheckboxFilterControl.supportAttribute(attribute.attrType, range[attribute.name]);
    }

    private static isMultipleFilter(criteria: Criteria | Criteria[]): boolean {
        return Array.isArray(criteria) && criteria.every((c) => CheckboxFilterControl.isCheckboxFilter(c));
    }

    private static isCheckboxFilter(criteria: Criteria) {
        return criteria.$type === "All"
            || CheckboxFilterControl.isUncheckCheckBox(criteria)
            || CheckboxFilterControl.isValidOrFilter(criteria)
            || criteria.$type === "Equal";
    }

    private static isUncheckCheckBox(criterias: Criteria | Criteria[]): boolean {
        if (!Array.isArray(criterias) || criterias.length !== 2) {
            return false;
        }

        if (criterias[0].$type !== "Equal" || criterias[1].$type !== "NotEqual") {
            return false;
        }

        return criterias[0].value == null && criterias[1].value == null;
    }

    private static isValidOrFilter(criteria: Criteria) {
        return criteria.$type === "Or" && criteria.criteria.every((crit) => crit.$type === "Equal");
    }

    static supportAttribute(attrType: string, range) {
        return !!range && range.$type === "ValueAttributeRange" &&  Object.keys(range.values).length <= es.spatialLimits.checkboxFilterMaxDistinctValues && (attrType === "STRING" || attrType === "BOOLEAN");
    }

    static fromCriteria(layerViewModel: LayerViewModel, criteria: Criteria, attribute: Attribute, ranges) {
        return new CheckboxFilterControl(layerViewModel, attribute, ranges[attribute.name]).init().then((filter: CheckboxFilterControl) => {
            filter.fromCriteria(criteria);
            return filter;
        });
    }
}
